type OnOff = 0 | 1; // TODO: change to boolean?

export type RpCheck2Type = 'USC10' | 'VALUE' | 'VALUE_BUFFER';

export type AssemblyTypeKey =
  | 'RP'
  | 'DC'
  | 'RPDA'
  | 'DCDA'
  | 'PVB'
  | 'SVB'
  | 'AVB'
  | 'AIR_GAP'
  | 'TYPE_2_BYPASS'
  | 'RPDA_2'
  | 'DCDA_2';

// RP, DC, RPDA, DCDA, PVBA, SVBA, AVB, AIR GAP, RPDA_2, DCDA_2
export type AssemblyTypeValue = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

export const AssemblyType: Record<AssemblyTypeKey, AssemblyTypeValue> = {
  RP: 1,
  DC: 2,
  RPDA: 3,
  DCDA: 4,
  PVB: 5,
  SVB: 6,
  AVB: 7,
  AIR_GAP: 8,
  TYPE_2_BYPASS: 9,
  RPDA_2: 10,
  DCDA_2: 11,
};

export type TestRepair = {
  type: 'repair';
  testDate?: string; // M/D/YYYY
  comment?: string;
  timestamp?: number;
  latitude?: number;
  longitude?: number;
};

export type TestResultBase = {
  type?: undefined | null;
  testDate?: string; // M/D/YYYY
  timestamp?: number;
  systemPsi?: number; // 3 digits, no decimal. Range should fall between >=30<=170, provide warning for values <=40 or >=150
  passed?: OnOff;
  latitude?: number;
  longitude?: number;
};

// TODO: finish reconciling this with Paul's new form requirements
export type TestResultRp = TestResultBase & {
  check1Apparent?: number; // NEW

  openedAt?: number; // decimal
  didNotOpen?: OnOff;

  // when water supplier rpCheck2Type is USC10, check2 can only be 'tight' or 'leaked'
  // when water supplier rpCheck2Type is VALUE, check2 is automatically 'other' and check2Other is required and has to be 1.0 or greater to pass
  check2?: 'leaked' | 'tight' | 'other';

  check2Other?: number; // decimal, deprecated

  pressureDrop?: number; // decimal, TODO: rename to check1 or check1Actual?

  reliefValveDripping?: 'no' | 'upon_arrival' | 'upon_closure'; // TODO: might be deprecated in favor of Paul's new "Failing Component(s)" list
};

export type TestResultDc = TestResultBase & {
  // For both of these:
  // Single digit and single decimal (eg. 3.3). If no decimal input, still format with a single decimal "1.0"
  // Provide warning if value >5.0, If value <1.0, the test fails
  check1?: number;
  check2?: number;
};

export type TestResultSvbPvb = TestResultBase & {
  openedAt?: number; // decimal
  pressureDrop?: number; // decimal
  didNotOpen?: OnOff;
};

export type TestResultType2Bypass = TestResultBase & {
  // For both of these:
  // Single digit and single decimal (eg. 3.3). If no decimal input, still format with a single decimal "1.0"
  // Provide warning if value >5.0, If value <1.0, the test fails
  bypassCheck?: number;
};

export type TestResultAvb = TestResultBase;

export type TestResult =
  | TestResultRp
  | TestResultDc
  | TestResultSvbPvb
  | TestResultType2Bypass
  | TestResultAvb;

export type BackflowAssemblyInfo = {
  modelId?: number | null; // integer
  waterSupplierId: number; // integer
  waterServiceId?: number | string | null;
  location: string | null;
  size?: number | null; // decimal
  serialNumber: string | null;
  type: AssemblyTypeValue;
  installDate?: string | null;

  testAnniversary?: string | null;
  hazardLevel?: null | 'LOW' | 'HIGH';
  hazardType?: null | string;
  protectionType?: null | 'SERVICE' | 'INTERNAL';
  orientation?: null | 'VERTICAL' | 'HORIZONTAL' | 'UNKNOWN';

  latitude?: number;
  longitude?: number;

  // Derived by the API from modelId; never persisted as independent form state.
  makeName?: string;
  modelName?: string;
};

// NEW, EXISTING, REMOVED, REPLACEMENT
export type InstallationStatusValue = 1 | 2 | 3 | 4;

export type InstallationStatusKey =
  | 'NEW'
  | 'EXISTING'
  | 'REMOVED'
  | 'REPLACEMENT';

export const InstallationStatus: Record<
  InstallationStatusKey,
  InstallationStatusValue
> = {
  NEW: 1,
  EXISTING: 2,
  REMOVED: 3,
  REPLACEMENT: 4,
};

export type JobFormDataBase = {
  comments?: string;
};

export type JobFormDataBackflow<
  ResultType extends TestResultBase = TestResult,
> = JobFormDataBase & {
  readonly originalBackflowAssembly?: BackflowAssemblyInfo; // assembly information from the original ordered job
  backflowAssembly?: BackflowAssemblyInfo; // new, replacement and updated assembly information

  // tester gauge and cert information

  // TODO: Set when assigned to tester?
  certificationNumber?: string | number;
  certificationState?: string;

  // Set by mobile app
  // TODO: There is also a gauge_id column on the backflow_test table. Which one should we use?
  gaugeCalibrated?: string;
  gaugeNumber?: string;
  gaugeId?: number;

  // frozen/cached values

  // TODO: No more storing this!
  // serviceAddress?: {
  //   string?: string;
  // };
  // TODO: No more storing this!
  // tester?: {
  //   address?: {
  //     string?: string;
  //     street1: string;
  //     street2?: string;
  //     city: string;
  //     state: string;
  //     zip: string | number;
  //   };
  //   id: number;
  //   name: string;
  //   signature: { version: number };
  // };

  // global assembly details
  improperlyInstalled?: OnOff;
  installationStatus?: InstallationStatusValue;
  repairsRequired?: OnOff;
  unableToTest?: OnOff; // Default to No. If Yes, don't require test data fields, but require a Comment, test result is Incomplete

  // backflow test attempts
  tests?:
    | []
    | [ResultType] // single passing test
    | [ResultType, ResultType] // failing then passing test
    | [ResultType, TestRepair] // failing test, repair
    | [ResultType, TestRepair, ResultType]; // failing test, repair, passing test

  // water connection information
  meterReading?: string | number; // string preserves entered formatting; number supports legacy callers
  shutoff1Position?: OnOff;
  shutoff2Position?: OnOff;
  shutoffMainPosition?: OnOff;
};

export type JobFormDataInput = JobFormDataBackflow;
